import { makeStyles, mergeClasses, tokens } from '@fluentui/react-components';
import { CheckmarkFilled } from '@fluentui/react-icons';

const useStyles = makeStyles({
    rail: {
        position: 'sticky',
        top: '20px',
        display: 'flex',
        flexDirection: 'column',
        gap: '2px',
        // Collapse below the two-column breakpoint.
        '@media (max-width: 960px)': {
            display: 'none',
        },
    },
    item: {
        display: 'flex',
        alignItems: 'center',
        gap: '11px',
        padding: '10px 12px',
        borderRadius: tokens.borderRadiusMedium,
        cursor: 'pointer',
        color: tokens.colorNeutralForeground2,
        fontSize: tokens.fontSizeBase300,
        fontWeight: tokens.fontWeightSemibold,
        border: 'none',
        background: 'transparent',
        textAlign: 'left',
        width: '100%',
        ':hover': {
            backgroundColor: tokens.colorSubtleBackgroundHover,
            color: tokens.colorNeutralForeground1,
        },
    },
    itemActive: {
        backgroundColor: tokens.colorBrandBackground2,
        color: tokens.colorBrandForeground1,
    },
    dot: {
        width: '22px',
        height: '22px',
        borderRadius: tokens.borderRadiusCircular,
        border: `1.5px solid ${tokens.colorNeutralStroke1}`,
        display: 'grid',
        placeItems: 'center',
        fontSize: '11px',
        fontWeight: tokens.fontWeightBold,
        color: tokens.colorNeutralForeground3,
        flex: 'none',
    },
    dotActive: {
        border: `1.5px solid ${tokens.colorBrandStroke1}`,
        color: tokens.colorBrandForeground1,
    },
    dotComplete: {
        backgroundColor: tokens.colorBrandBackground,
        border: `1.5px solid ${tokens.colorBrandBackground}`,
        color: tokens.colorNeutralForegroundOnBrand,
    },
});

export interface SectionRailItem {
    key: string;
    label: string;
    complete: boolean;
}

interface SectionRailProps {
    items: SectionRailItem[];
    activeKey: string;
    onJump: (key: string) => void;
}

/** Sticky left nav for the single-page form: status dot + active highlight. */
const SectionRail = ({ items, activeKey, onJump }: SectionRailProps) => {
    const styles = useStyles();

    return (
        <nav className={styles.rail} aria-label="Form sections">
            {items.map((item, index) => {
                const active = item.key === activeKey;
                return (
                    <button
                        key={item.key}
                        type="button"
                        className={mergeClasses(styles.item, active && styles.itemActive)}
                        aria-current={active ? 'step' : undefined}
                        onClick={() => onJump(item.key)}
                    >
                        <span
                            className={mergeClasses(
                                styles.dot,
                                active && styles.dotActive,
                                item.complete && styles.dotComplete,
                            )}
                        >
                            {item.complete ? <CheckmarkFilled fontSize={13} /> : index + 1}
                        </span>
                        {item.label}
                    </button>
                );
            })}
        </nav>
    );
};

export default SectionRail;
